import './qbooksAccountLineItemsSection.scss';

import { Button, DropdownEditor } from '@approvalmax/ui';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { LineItemsGroupEdit } from 'pages/requestForm/components';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';

import {
    addQbooksAccountLineItem,
    changeQbooksLineAmountType,
    cloneQbooksAccountLineItems,
    removeQbooksAccountLineItems,
    reorderQbooksAccountLineItem,
} from '../../../../actions';
import { LineItemsTable, SectionContainer } from '../../../../components';
import { findLineAmountType, lineAmountTypeReferences } from '../../../../data/qbooks/statics/lineAmountType';
import { getShowValidationErrors } from '../../../../selectors/pageSelectors';
import { getQBooksContext } from '../../../../selectors/qbooks';
import {
    useAccountLineItems,
    useColumnDefinitions,
    useGroupEditItems,
    useIsQBooksAccountReadonly,
    useLineItems,
    useRequestExpanded,
    useTaxCodes,
} from './hooks';
import { messages } from './QBooksAccountLineItemsSection.messages';
import type { QBooksAccountLineItemsSectionProps } from './QBooksAccountLineItemsSection.types';

const bem = bemFactory.block('reqf-qbooks-acc-line-item-section');
const qa = bemFactory.qa('reqf-qbooks-acc-line-item-section');

export const QBooksAccountLineItemsSection: FC<QBooksAccountLineItemsSectionProps> = memo((props) => {
    const { onChange, onRemoveMatching } = props;

    const dispatch = useDispatch();
    const request = useRequestExpanded();
    const qbooksContext = useSelector(getQBooksContext);

    const taxCodesList = useTaxCodes();
    const lineItems = useLineItems();
    const accountLineItems = useAccountLineItems();

    const hasOtherLineItems = lineItems.some((lineItem) => !lineItem.empty);

    const isQBooksAccountReadonly = useIsQBooksAccountReadonly();
    const isValidationErrorsShown = useSelector(getShowValidationErrors);

    const {
        isGroupCloneVisible,
        isGroupEditVisible,
        isGroupRemoveVisible,
        isShowGroupButtons,
        openGroupEdit,
        handleGroupClone,
        handleGroupRemove,
        isGroupEditPopupVisible,
        groupItems,
        closeGroupEdit,
    } = useGroupEditItems({
        accountLineItems,
        taxCodesList,
        accountVisible: !isQBooksAccountReadonly,
        taxVisible: request.details.lineAmountType !== domain.LineAmountType.NoTax,
        classVisible: qbooksContext.hasClassFeature,
        customerVisible: qbooksContext.hasCustomerFeature,
    });

    const tableDef = useColumnDefinitions({
        qa,
        bem,
        onChange,
        onRemoveMatching,
    });

    return (
        <SectionContainer
            className={bem()}
            qa={qa()}
            noPadding
            title={
                <div className={bem('header')}>
                    <div className={bem('header-text')}>{messages.headerText}</div>

                    {qbooksContext.hasTaxesFeature && (
                        <div className={bem('header-fields')}>
                            <div className={bem('header-fields-label')}>{messages.lineAmountTypeLabel}</div>

                            <PlainDataProvider items={lineAmountTypeReferences}>
                                <DropdownEditor
                                    qa={qa('line-amount-type-editor')}
                                    className={bem('header-fields-line-amount-type')}
                                    value={findLineAmountType(request.details.lineAmountType)}
                                    onChange={(value: { id: domain.LineAmountType }) =>
                                        dispatch(changeQbooksLineAmountType(value.id))
                                    }
                                    buttonComponent={DropdownEditor.NoFilterButton}
                                />
                            </PlainDataProvider>
                        </div>
                    )}

                    {isShowGroupButtons && (
                        <div className={bem('group-actions')}>
                            {isGroupEditVisible && (
                                <Button preset='small' execute={openGroupEdit}>
                                    {messages.groupEdit}
                                </Button>
                            )}

                            {isGroupCloneVisible && (
                                <Button preset='small' execute={handleGroupClone}>
                                    {messages.groupClone}
                                </Button>
                            )}

                            {isGroupRemoveVisible && (
                                <Button preset='small' execute={handleGroupRemove}>
                                    {messages.groupRemove}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            }
        >
            <div className={bem('content')}>
                <LineItemsTable
                    canAddLine
                    className={bem('content-table')}
                    columnDefinitions={tableDef}
                    lineItems={accountLineItems}
                    invalid={isValidationErrorsShown && accountLineItems.every((li) => li.empty) && !hasOtherLineItems}
                    onReorder={(oldIndex, newIndex) => dispatch(reorderQbooksAccountLineItem(oldIndex, newIndex))}
                    onRemoveLine={(lineId) => dispatch(removeQbooksAccountLineItems([lineId]))}
                    onCloneLine={(lineId) => dispatch(cloneQbooksAccountLineItems([lineId]))}
                    onAddLine={() => dispatch(addQbooksAccountLineItem())}
                />
            </div>

            {isGroupEditPopupVisible && <LineItemsGroupEdit onClose={closeGroupEdit} items={groupItems} />}
        </SectionContainer>
    );
});

QBooksAccountLineItemsSection.displayName = 'QBooksAccountLineItemsSection';
